<template>
  <div class="image-cropper">
    <div
      class="image-cropper-overlay"
      :class="{ 'image-cropper-overlay-inline': inline }"
      v-if="dataUrl"
    >
      <div class="image-cropper-mark" v-if="!inline">
        <a
          @click="cancel"
          class="image-cropper-close"
          :title="labels.cancel"
          href="javascript:;"
          >&times;</a
        >
      </div>

      <div class="image-cropper-container">
        <div class="image-cropper-image-container">
          <img
            ref="img"
            :src="dataUrl"
            alt
            @load.stop="createCropper"
            @error="onImgElementError"
          />
        </div>

        <div class="image-cropper-footer">
          <button @click.stop.prevent="cancel" class="image-cropper-btn">
            {{ labels.cancel }}
          </button>

          <button @click.stop.prevent="submit" class="image-cropper-btn">
            {{ labels.submit }}
          </button>
        </div>
      </div>
    </div>

    <input
      v-if="!file"
      :accept="cleanedMimes"
      class="image-cropper-img-input"
      ref="input"
      type="file"
      @change="onFileInputChange"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
// import { putObject } from "../../services/minio";
import { resizeImage } from "../../services/media";
import "cropperjs/dist/cropper.css";
import Cropper from "cropperjs/dist/cropper.min.js";
import mime from "mime/lite";
// import { generateId } from "../../utils/helpers";

export default {
  name: "PhotoCropper",
  model: {
    prop: "trigger",
    event: "triggered"
  },

  props: {
    trigger: {
      type: Boolean,
      default: false
    },

    email: {
      type: String,
      default: null
    },

    file: {
      type: File
    },

    tenant: {
      type: String
    },

    requestOptions: {
      type: Object,
      default() {
        return {
          method: "POST"
        };
      }
    },

    uploadFileField: {
      type: String,
      default: "file"
    },

    uploadFileName: {
      type: [String, Function]
    },

    uploadFormData: {
      type: FormData,
      default() {
        return new FormData();
      }
    },

    cropperOptions: {
      type: Object,
      default() {
        return {
          aspectRatio: 1,
          autoCropArea: 1,
          viewMode: 1,
          movable: false,
          zoomable: false
        };
      }
    },

    outputOptions: {
      type: Object
    },

    outputMime: {
      type: String,
      default: null
    },

    outputQuality: {
      type: Number,
      default: 0.9
    },

    width: {
      type: Number,
      default: 512
    },

    mimes: {
      type: String,
      default: "image/png, image/gif, image/jpeg, image/bmp, image/x-icon"
    },

    // capture: {
    //   type: String
    // },

    labels: {
      type: Object,
      default() {
        return {
          submit: "Ok",
          cancel: "Cancel"
        };
      }
    },

    inline: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      cropper: undefined,
      dataUrl: undefined,
      fileName: undefined,
      mimeType: undefined
    };
  },

  computed: {
    ...mapGetters(["user"]),
    cleanedMimes() {
      if (!this.mimes)
        throw new Error("vue-image-cropper: mimes prop cannot be empty");

      return this.mimes.trim().toLowerCase();
    }
  },

  watch: {
    trigger(value) {
      if (!value) return;

      if (this.file) {
        this.onFileChange(this.file);
      } else {
        this.pickImage();
      }

      this.$emit("triggered", false);
    }
  },

  mounted() {
    this.$emit("triggered", false);
  },

  methods: {
    destroy() {
      if (this.cropper) this.cropper.destroy();

      if (this.$refs.input) this.$refs.input.value = "";

      this.dataUrl = undefined;
    },

    submit() {
      this.uploadImage();
    },

    cancel() {
      this.$emit("cancel");
      this.destroy();
    },

    onImgElementError() {
      this.$emit("error", {
        type: "load",
        message: "File loading failed"
      });
      this.destroy();
    },

    pickImage() {
      if (this.$refs.input) this.$refs.input.click();
    },

    onFileChange(file) {
      if (this.cleanedMimes === "image/*") {
        if (file.type.split("/")[0] !== "image") {
          this.$emit("error", {
            type: "user",
            message: "File type not correct"
          });
          return;
        }
      } else if (this.cleanedMimes) {
        const correctType = this.cleanedMimes
          .split(", ")
          .find(mime => mime === file.type);

        if (!correctType) {
          this.$emit("error", {
            type: "user",
            message: "File type not correct"
          });
          return;
        }
      }

      const reader = new FileReader();
      reader.onload = e => {
        this.dataUrl = e.target.result;
      };

      reader.readAsDataURL(file);

      this.fileName = file.name || "unknown";
      this.mimeType = file.type;

      this.$emit("changed", {
        file,
        reader
      });
    },

    onFileInputChange(e) {
      if (!e.target.files || !e.target.files[0]) return;

      this.onFileChange(e.target.files[0]);
    },

    createCropper() {
      this.cropper = new Cropper(this.$refs.img, this.cropperOptions);
    },

    getFilename(blob) {
      const extension = mime.getExtension(blob.type);

      // Default logic
      if (!this.uploadFileName) {
        let actualFilename = this.fileName;

        const filenameParts = this.fileName.split(".");
        if (filenameParts.length > 1)
          actualFilename = filenameParts.slice(0, -1).join(".");

        return `${actualFilename}.${extension}`;
      }

      // User provided filename
      if (typeof this.uploadFileName === "string") return this.uploadFileName;

      if (typeof this.uploadFileName === "function")
        return this.uploadFileName({
          filename: this.fileName,
          mime: blob.type,
          extension
        });

      return `unknown.${extension}`;
    },

    uploadImage() {
      this.cropper.getCroppedCanvas(this.outputOptions).toBlob(
        async blob => {
          const formData = new FormData();
          let fileName = this.email
            ? `${this.email.hashCode()}.jpg`
            : `${this.tenant.hashCode()}.jpg`;
          formData.append("image", blob, fileName);
          resizeImage({
            formData: formData,
            width: this.width,
            fileName: fileName,
            tenant: this.tenant
          }).then(convertedFileName => {
            let data = {
              photo: `${process.env.VUE_APP_BASE_URL.toCorrectDomain()}/${
                process.env.VUE_APP_CDN_URL
              }${this.tenant}/${convertedFileName}`
            };
            this.$emit("completed", data);
            this.destroy();
          });
        },
        this.outputMime || this.mimeType,
        this.outputQuality
      );
    }
  }
};
</script>
